import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Edit, 
  Trash2, 
  Building2, 
  User, 
  ArrowRight,
  Plus,
  X,
  Save,
  AlertTriangle,
  CheckSquare,
  FolderOpen
} from 'lucide-react';
import StatusBadge from '../components/ui/StatusBadge';

const TeamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { teams, users, departments, tasks, projects, updateTeam, deleteTeam, updateUser } = useData();
  const { currentUser } = useAuth();
  
  const [team, setTeam] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [newSubtask, setNewSubtask] = useState('');
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);

  useEffect(() => {
    if (teams && id) {
      const foundTeam = teams.find(t => t.id === id);
      if (foundTeam) {
        setTeam(foundTeam);
        setEditForm({
          name: foundTeam.name || '',
          description: foundTeam.description || '',
          departmentId: foundTeam.departmentId || '',
          managerId: foundTeam.managerId || '',
          isActive: foundTeam.isActive !== undefined ? foundTeam.isActive : true,
          subtasks: foundTeam.subtasks || []
        });
      }
      setLoading(false);
    }
  }, [teams, id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">צוות לא נמצא</h1>
          <p className="text-gray-600 mb-4">הצוות שחיפשת לא קיים או נמחק</p>
          <button
            onClick={() => navigate('/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            חזור לצוותים
          </button>
        </div>
      </div>
    );
  }

  // Check if user has access to edit this team
  const canEdit = currentUser && (
    currentUser.role === 'org_admin' || 
    currentUser.role === 'ceo' || 
    currentUser.role === 'deputy_ceo' ||
    (currentUser.role === 'dept_manager' && team.departmentId === currentUser.departmentId) ||
    (currentUser.role === 'team_manager' && team.managerId === currentUser.id)
  );

  const canDelete = currentUser && (
    currentUser.role === 'org_admin' || 
    currentUser.role === 'ceo' || 
    currentUser.role === 'deputy_ceo'
  );

  const teamManager = users.find(u => u.id === team.managerId);
  // מצא משתמשים ששייכים לצוות - תמיכה במספר צוותים
 const teamMembers = users.filter(u => {
  // לא להציג את המנהל פעמיים
  if (u.id === team.managerId) return false;
  
  // בדיקה 1: האם למשתמש יש מערך teamIds והוא כולל את הצוות הזה
  if (u.teamIds && Array.isArray(u.teamIds)) {
    if (u.teamIds.includes(team.id)) {
      return true;
    }
  }
  
  // בדיקה 2: האם למשתמש יש teamId בודד ששווה לצוות הזה  
  if (u.teamId && u.teamId === team.id) {
    return true;
  }
  
  // אם אף בדיקה לא עברה
  return false;
});
  const teamDepartment = departments.find(d => d.id === team.departmentId);

  const handleSave = async () => {
    try {
      await updateTeam(team.id, editForm);
      setTeam({ ...team, ...editForm });
      setIsEditing(false);
      alert('הצוות עודכן בהצלחה!');
    } catch (error) {
      console.error('Error updating team:', error);
      alert('שגיאה בעדכון הצוות');
    }
  };

  const handleDelete = async () => {
    try {
      // Remove team from all team members
      const updatePromises = teamMembers.map(member => 
        updateUser(member.id, { 
          teamId: null, 
          teamManagerId: null 
        })
      );
      
      // גם הסר את המנהל מהצוות
      if (teamManager) {
        updatePromises.push(
          updateUser(team.managerId, { 
            teamId: null, 
            teamManagerId: null 
          })
        );
      }
      
      await Promise.all(updatePromises);
      await deleteTeam(team.id);
      
      alert('הצוות נמחק בהצלחה!');
      navigate('/teams');
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('שגיאה במחיקת הצוות');
    }
  };

  const removeMember = async (memberId) => {
    try {
      const user = users.find(u => u.id === memberId);
      
      // הסר את הצוות מהמערך
      const currentTeamIds = user.teamIds || [];
      const updatedTeamIds = currentTeamIds.filter(id => id !== team.id);
      
      await updateUser(memberId, { 
        teamIds: updatedTeamIds,
        // אם זה הצוות היחיד, נקה גם את teamId
        teamId: updatedTeamIds.length > 0 ? user.teamId : null
      });
      
      alert('העובד הוסר מהצוות בהצלחה!');
      
      // רענן את הדף
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (error) {
      console.error('Error removing member:', error);
      alert('שגיאה בהסרת העובד מהצוות');
    }
  };

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      alert('אנא בחר לפחות עובד אחד להוספה לצוות');
      return;
    }
    
    try {
      const updatePromises = selectedMembers.map(memberId => {
        const user = users.find(u => u.id === memberId);
        
        // צור מערך של צוותים אם לא קיים
        const currentTeamIds = user.teamIds || [];
        
        // הוסף את הצוות החדש למערך (אם הוא לא קיים כבר)
        const updatedTeamIds = currentTeamIds.includes(team.id) 
          ? currentTeamIds 
          : [...currentTeamIds, team.id];
        
        return updateUser(memberId, {
          teamIds: updatedTeamIds,
          // שמור גם teamId בודד לתאימות לאחור
          teamId: team.id
        });
      });
      
      await Promise.all(updatePromises);
      
      alert(`${selectedMembers.length} עובדים נוספו לצוות בהצלחה!`);
      setShowAddMembersModal(false);
      setSelectedMembers([]);
      setSearchTerm('');
      
      // רענן את הדף
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error adding members:', error);
      alert('שגיאה בהוספת חברים לצוות');
    }
  };

  const availableUsers = users.filter(u => {
    // לא לסנן עובדים שכבר בצוות אחר - מאפשר ריבוי צוותים
    
    // לא להציג את העובד אם הוא כבר בצוות הנוכחי
    if (u.teamId === team.id) return false;
    if (u.teamIds && Array.isArray(u.teamIds) && u.teamIds.includes(team.id)) return false;
    
    // לא להציג מנהלים
    if (['org_admin', 'ceo', 'deputy_ceo'].includes(u.role)) return false;
    
    // סינון לפי חיפוש
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        u.name.toLowerCase().includes(search) ||
        u.email.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">פרטי צוות</h1>
              <p className="text-gray-600 mt-2">צפייה ועריכה של פרטי הצוות</p>
            </div>
            <div className="flex items-center gap-3">
              {canEdit && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  {isEditing ? <X size={16} /> : <Edit size={16} />}
                  {isEditing ? 'ביטול עריכה' : 'ערוך צוות'}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  מחק צוות
                </button>
              )}
              <button
                onClick={() => navigate('/teams')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <ArrowRight size={16} />
                חזור לצוותים
              </button>
            </div>
          </div>
        </div>

        {/* Team Tasks and Projects Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('tasks')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'tasks'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CheckSquare size={20} />
                  משימות
                </div>
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'projects'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen size={20} />
                  פרויקטים
                </div>
              </button>
            </nav>
          </div>
          <div className="p-6">
            {activeTab === 'tasks' ? (
              <div>
                <h2 className="text-xl font-semibold mb-4">משימות הצוות</h2>
                {(() => {
                  const teamTasks = tasks.filter(task => task.teamId === team.id);
                  
                  if (teamTasks.length === 0) {
                    return (
                      <p className="text-gray-500 text-center py-8">אין משימות שהוקצו לצוות זה</p>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {teamTasks.map(task => (
                        <div key={task.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">{task.title}</h4>
                              <p className="text-sm text-gray-600">{task.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                {task.assigneeId && (
                                  <span>מוקצה: {users.find(u => u.id === task.assigneeId)?.name || 'לא ידוע'}</span>
                                )}
                                {task.dueDate && (
                                  <span>דדליין: {new Date(task.dueDate).toLocaleDateString('he-IL')}</span>
                                )}
                                {task.estimate > 0 && (
                                  <span>הערכה: {task.estimate} שעות</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <StatusBadge status={task.status || 'במסלול'} size="sm" />
                              <div className="text-sm text-gray-600 mt-1">
                                {task.priority === 'important_urgent' ? 'חשוב ודחוף' :
                                 task.priority === 'important_not_urgent' ? 'חשוב ולא דחוף' :
                                 task.priority === 'not_important_urgent' ? 'דחוף ולא חשוב' :
                                 task.priority === 'not_important_not_urgent' ? 'לא חשוב ולא דחוף' :
                                 task.priority}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold mb-4">פרויקטי הצוות</h2>
                {(() => {
                  // Find projects assigned directly to the team OR projects that have tasks assigned to this team
                  const directTeamProjects = projects.filter(project => project.teamId === team.id);
                  const taskProjectIds = [...new Set(tasks.filter(task => task.teamId === team.id).map(task => task.projectId))];
                  const taskBasedProjects = projects.filter(project => taskProjectIds.includes(project.id));
                  
                  // Debug info
                  console.log('Team ID:', team.id);
                  console.log('Direct team projects:', directTeamProjects.length, directTeamProjects.map(p => p.name));
                  console.log('Task project IDs:', taskProjectIds);
                  console.log('Task-based projects:', taskBasedProjects.length, taskBasedProjects.map(p => p.name));
                  
                  // Combine both types and remove duplicates
                  const allTeamProjects = [...directTeamProjects];
                  taskBasedProjects.forEach(project => {
                    if (!allTeamProjects.find(p => p.id === project.id)) {
                      allTeamProjects.push(project);
                    }
                  });
                  
                  console.log('All team projects:', allTeamProjects.length, allTeamProjects.map(p => p.name));
                  
                  if (allTeamProjects.length === 0) {
                    return (
                      <p className="text-gray-500 text-center py-8">אין פרויקטים לצוות זה</p>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {allTeamProjects.map(project => {
                        const projectTasks = tasks.filter(task => task.projectId === project.id && task.teamId === team.id);
                        const completedProjectTasks = projectTasks.filter(task => task.status === 'הושלם');
                        
                        return (
                          <div key={project.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium">{project.name}</h4>
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                    {departments.find(d => d.id === project.departmentId)?.name || 'לא ידוע'}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{project.description}</p>
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                  <span>תחום: {project.scope === 'personal' ? 'אישי' : 'ניהולי'}</span>
                                  {project.startDate && (
                                    <span>התחלה: {new Date(project.startDate).toLocaleDateString('he-IL')}</span>
                                  )}
                                  {project.endDate && (
                                    <span>סיום: {new Date(project.endDate).toLocaleDateString('he-IL')}</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <StatusBadge 
                                  status={(() => {
                                    if (projectTasks.length === 0) return 'ממתין לעבודה';
                                    if (completedProjectTasks.length === projectTasks.length) return 'פרויקט הושלם';
                                    
                                    const activeTasks = projectTasks.filter(task => task.status !== 'הושלם');
                                    if (activeTasks.length > 0) return 'פרויקט בתהליך';
                                    
                                    return 'ממתין לעבודה';
                                  })()} 
                                  size="sm" 
                                />
                                <div className="text-sm text-gray-600 mt-1">
                                  {projectTasks.length} משימות
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Team Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">מידע כללי</h2>
          
          {isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">מחלקה</label>
                <select
                  value={editForm.departmentId}
                  onChange={(e) => setEditForm({ ...editForm, departmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">בחר מחלקה</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                <select
                  value={editForm.managerId}
                  onChange={(e) => setEditForm({ ...editForm, managerId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">בחר מנהל</option>
                  {users.filter(u => ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager'].includes(u.role)).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    className="ml-2"
                  />
                  <span className="text-sm font-medium text-gray-700">צוות פעיל</span>
                </label>
              </div>
              
              {/* Subtasks */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">תתי משימות</label>
                <div className="space-y-3">
                  {/* Existing Subtasks */}
                  {editForm.subtasks && editForm.subtasks.length > 0 && (
                    <div className="space-y-2">
                      {editForm.subtasks.map((subtask, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                          <span className="flex-1 text-sm">{subtask}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedSubtasks = editForm.subtasks.filter((_, i) => i !== index);
                              setEditForm({...editForm, subtasks: updatedSubtasks});
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="מחק תת משימה"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add New Subtask */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="הוסף תת משימה חדשה..."
                      value={newSubtask || ''}
                      onChange={(e) => setNewSubtask(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newSubtask.trim()) {
                          const updatedSubtasks = [...(editForm.subtasks || []), newSubtask.trim()];
                          setEditForm({...editForm, subtasks: updatedSubtasks});
                          setNewSubtask('');
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newSubtask && newSubtask.trim()) {
                          const updatedSubtasks = [...(editForm.subtasks || []), newSubtask.trim()];
                          setEditForm({...editForm, subtasks: updatedSubtasks});
                          setNewSubtask('');
                        }
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-1"
                    >
                      <Plus size={14} />
                      הוסף
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-2">
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Save size={16} />
                  שמור שינויים
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-sm font-medium text-gray-500">שם הצוות</span>
                <p className="text-lg font-medium text-gray-900">{team.name}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">תיאור</span>
                <p className="text-lg text-gray-900">{team.description || 'אין תיאור'}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">מחלקה</span>
                <p className="text-lg text-gray-900">{teamDepartment?.name || 'לא מוגדר'}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">מנהל צוות</span>
                <p className="text-lg text-gray-900">{teamManager?.name || 'לא מוגדר'}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">סטטוס</span>
                <div className="mt-1">
                  <StatusBadge 
                    status={team.isActive ? 'פעיל' : 'לא פעיל'} 
                    size="md" 
                  />
                </div>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">מספר חברים</span>
                <p className="text-lg text-gray-900">{teamMembers.length + (teamManager ? 1 : 0)}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">משימות פעילות</span>
                <p className="text-lg text-gray-900">{tasks.filter(task => task.teamId === team.id && task.status !== 'הושלם').length}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">משימות הושלמו</span>
                <p className="text-lg text-gray-900">{tasks.filter(task => task.teamId === team.id && task.status === 'הושלם').length}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">פרויקטים</span>
                <p className="text-lg text-gray-900">{(() => {
                  // Count projects assigned directly to the team OR projects that have tasks assigned to this team
                  const directTeamProjects = projects.filter(project => project.teamId === team.id);
                  const taskProjectIds = [...new Set(tasks.filter(task => task.teamId === team.id).map(task => task.projectId))];
                  const taskBasedProjects = projects.filter(project => taskProjectIds.includes(project.id));
                  
                  // Combine both types and remove duplicates
                  const allTeamProjects = [...directTeamProjects];
                  taskBasedProjects.forEach(project => {
                    if (!allTeamProjects.find(p => p.id === project.id)) {
                      allTeamProjects.push(project);
                    }
                  });
                  
                  return allTeamProjects.length;
                })()}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">תתי משימות</span>
                <p className="text-lg text-gray-900">{team.subtasks ? team.subtasks.length : 0}</p>
              </div>
            </div>
          )}
        </div>

{/* Team Members */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">חברי הצוות</h2>
            {canEdit && (
              <button
                onClick={() => setShowAddMembersModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Plus size={16} />
                הוסף חברים
              </button>
            )}
          </div>          
          {/* Subtasks Display */}
          {team.subtasks && team.subtasks.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-medium text-blue-900 mb-3">תתי משימות של הצוות</h3>
              <div className="space-y-2">
                {team.subtasks.map((subtask, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <CheckSquare size={16} className="text-blue-600" />
                    <span className="text-sm text-gray-700">{subtask}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Team Manager */}
            {teamManager && (
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{teamManager.name}</p>
                    <p className="text-sm text-gray-600">{teamManager.email}</p>
                    <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                      מנהל צוות
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Team Members */}
            {teamMembers.map(member => (
              <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-600">{member.email}</p>
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                      חבר צוות
                    </span>
                  </div>
                </div>
                
                {canEdit && (
                  <button
                    onClick={() => removeMember(member.id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                    title="הסר מהצוות"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            
            {teamMembers.length === 0 && !teamManager && (
              <p className="text-gray-500 text-center py-8">אין חברים בצוות זה</p>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle size={24} className="text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">מחיקת צוות</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                האם אתה בטוח שברצונך למחוק את הצוות "{team.name}"? 
                פעולה זו תסיר את כל חברי הצוות מהצוות ולא ניתן יהיה לשחזר אותה.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  ביטול
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  מחק צוות
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Members Modal */}
        {showAddMembersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">הוסף חברים לצוות</h3>
                <button
                  onClick={() => {
                    setShowAddMembersModal(false);
                    setSelectedMembers([]);
                    setSearchTerm('');
                  }}
                  className="text-white hover:bg-green-700 rounded p-1"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6">
                {/* Search Bar */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">חיפוש עובדים</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="חפש לפי שם או מייל..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                
                {/* Available Users List */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    עובדים זמינים ({availableUsers.length})
                  </label>
                  
                  {availableUsers.length > 0 ? (
                    <div className="max-h-96 overflow-y-auto border rounded-lg">
                      {availableUsers.map(user => (
                        <div
                          key={user.id}
                          className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${
                            selectedMembers.includes(user.id) ? 'bg-green-50' : ''
                          }`}
                          onClick={() => {
                            if (selectedMembers.includes(user.id)) {
                              setSelectedMembers(selectedMembers.filter(id => id !== user.id));
                            } else {
                              setSelectedMembers([...selectedMembers, user.id]);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-gray-600">{user.email}</p>
                              <p className="text-xs text-gray-500">
                                {departments.find(d => d.id === user.departmentId)?.name || 'ללא מחלקה'}
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={selectedMembers.includes(user.id)}
                              onChange={() => {}}
                              className="ml-4"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 border rounded-lg">
                      <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p>אין עובדים זמינים להוספה</p>
                      <p className="text-sm mt-2">כל העובדים כבר משוייכים לצוותים</p>
                    </div>
                  )}
                </div>
                
                {/* Selected Count */}
                {selectedMembers.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      נבחרו {selectedMembers.length} עובדים להוספה
                    </p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowAddMembersModal(false);
                      setSelectedMembers([]);
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    ביטול
                  </button>
                  <button
                    onClick={handleAddMembers}
                    disabled={selectedMembers.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    הוסף לצוות ({selectedMembers.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDetail;